import { useBreakpoint } from '@bridebook/ui/src/utils/hooks/use-breakpoint';
import PageHead from '@bridebook/web/app-basic/components/head/head';
import { basicGSSPWrapper } from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { getWeddingWebsiteOgImageUrl } from '@bridebook/web/app-basic/lib/wedding-website/metadata/get-og-image-url';
import { useWeddingWebsiteMetadata } from '@bridebook/web/app-basic/lib/wedding-website/metadata/use-wedding-website-metadata';
import GuestRsvp from '@bridebook/web/components/wedding-website/guest-rsvp/guest-rsvp';
import LayoutWebsite from '@bridebook/web/components/wedding-website/layout-website';
import { THEME_BRIDEBOOK } from '@bridebook/web/lib/wedding-website/constants/themes';
import { GetWeddingWebsiteDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]';
import { AppType, NextPageWithLayout } from '@bridebook/web/pages/_app';
import { fetchWeddingWebsite } from '@bridebook/web/app-basic/lib/wedding-website/api';
import { getBaseURLFromRequest } from '@bridebook/toolbox/src/getBaseURL';

interface Props {
  initialWeddingWebsiteData: GetWeddingWebsiteDataResponse;
}

type LayoutWrapperProps = {
  children: React.ReactNode;
  initialWeddingWebsiteData: GetWeddingWebsiteDataResponse;
};

const LayoutWrapper = ({ children, initialWeddingWebsiteData }: LayoutWrapperProps) => {
  const withFooter = useBreakpoint('mobileLg');

  return (
    <LayoutWebsite withFooter={withFooter} initialWeddingWebsiteData={initialWeddingWebsiteData}>
      {({ websiteData }) => (
        <>
          {children}
          <GuestRsvp websiteData={websiteData} theme={THEME_BRIDEBOOK} />
        </>
      )}
    </LayoutWebsite>
  );
};

const PublicRSVPWebsitePage: NextPageWithLayout<Props> = ({ initialWeddingWebsiteData }: Props) => {
  const partners = initialWeddingWebsiteData.wedding.partners;
  const date = initialWeddingWebsiteData.wedding.date;
  const { imageText, rsvpTitle, rsvpDescription } = useWeddingWebsiteMetadata({
    partners,
    date,
  });

  const ogImageUrl = getWeddingWebsiteOgImageUrl({
    text: imageText,
    palette: initialWeddingWebsiteData.website.config.theme.palette,
    fontPair: initialWeddingWebsiteData.website.config.theme.font,
    photoPath: initialWeddingWebsiteData.metadataPhotoPath,
    layout: initialWeddingWebsiteData.website.config.layout,
    motif: initialWeddingWebsiteData.motifOgTheme,
  });

  return (
    <PageHead
      title={rsvpTitle}
      description={rsvpDescription}
      ogTitle={rsvpTitle}
      ogDescription={rsvpDescription}
      ogImage={ogImageUrl}
      ogType="website"
    />
  );
};

PublicRSVPWebsitePage.getLayout = function getLayout(_page: React.ReactElement<Props>) {
  const { initialWeddingWebsiteData } = _page.props;
  return (
    <LayoutWrapper initialWeddingWebsiteData={initialWeddingWebsiteData}>{_page}</LayoutWrapper>
  );
};

export const getServerSideProps = basicGSSPWrapper(['weddingWebsite'], async ({ context }) => {
  const { websiteId } = context.params as { websiteId: string };

  try {
    const baseUrl = getBaseURLFromRequest(context.req);
    const { data, httpStatus } = await fetchWeddingWebsite(websiteId, {}, baseUrl);
    if (httpStatus === 200 || httpStatus === 401) {
      return {
        props: {
          initialWeddingWebsiteData: data,
        },
      };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch wedding website server side', e);
    return { notFound: true };
  }

  return { notFound: true };
});

PublicRSVPWebsitePage.AppType = AppType.Basic;

export default PublicRSVPWebsitePage;
